// 
import React, { useState, useRef, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";

// RSuite Components
import {
  Form,
  Button,
  Message,
  Loader,
  Whisper,
  Tooltip,
  Schema,
  SelectPicker,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaKey,
  FaUserShield,
  FaUserTie,
  FaCashRegister,
  FaClipboardList,
  FaPlus,
} from "react-icons/fa";

const AddUser = () => {
  const { addUserToList } = useContext(MotherContext);
  const navigate = useNavigate();
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // Role Options
  const roleOptions = [
    { label: "Master Admin", value: "1", icon: <FaUserShield /> },
    { label: "Admin", value: "2", icon: <FaUserTie /> },
    { label: "Account", value: "3", icon: <FaCashRegister /> },
    { label: "Sales Manager", value: "4", icon: <FaUser /> },
  ];

  // Custom render for SelectPicker
  const renderMenuItem = (label, item) => {
    return (
      <div className="d-flex align-items-center gap-2">
        {item.icon}
        <span>{label}</span>
      </div>
    );
  };

  // Validation Schema
  const { StringType } = Schema.Types;

  const model = Schema.Model({
    name: StringType().isRequired("Name is required."),
    email: StringType()
      .isEmail("Please enter a valid email.")
      .isRequired("Email is required."),
    phone: StringType(),
    password: StringType()
      .minLength(8, "Password must be at least 8 characters.")
      .isRequired("Password is required."),
    who_am_i: StringType().isRequired("Role is required."),
  });

  // Handle Submit
  const handleSubmit = async () => {
  if (!formRef.current.check()) return;
  setLoading(true);

  try {
    const res = await AxiosAuthInstance.post("users", formValue);

    if (res.data.status) {
    addUserToList({  // ✅ কনটেক্সটে যোগ করুন
    id: res.data.user?.id || Date.now(),
    name: formValue.name,
    email: formValue.email,
    phone: formValue.phone,
    who_am_i: parseInt(formValue.who_am_i),
    user_status: 1,
  });

  toaster.push(<Message type="success">User created!</Message>, { placement: "topEnd" });
  navigate("/user_setting");
}
  } catch (err) {
    const serverErrors = err.response?.data?.errors || {};
    if (Object.keys(serverErrors).length > 0) {
      const formattedErrors = Object.keys(serverErrors).reduce((acc, key) => {
        acc[key] = serverErrors[key][0];
        return acc;
      }, {});
      if (serverErrors.limit) {
        toaster.push(
          <Message type="warning" duration={5000}>
            {serverErrors.limit[0]}
          </Message>,
          { placement: "topEnd" }
        );
      } else {
        setFormError(formattedErrors);
      }
    } else {
      toaster.push(
        <Message type="error" duration={5000}>
          Something went wrong. Please try again later.
        </Message>,
        { placement: "topEnd" }
      );
    }
  } finally {
    setLoading(false);
  }
};


  const handleCancel = () => {
    navigate("/user_setting");
  };

  return (
    <>
      <Breadcrumb title="Add User" />

      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          {/* Header */}
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPlus /> Add New User
            </h5>
            <Link to="/user_setting" className="text-white text-decoration-none">
              <Whisper placement="left" speaker={<Tooltip>User List</Tooltip>}>
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>

          {/* Body */}
          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
              model={model}
            >
              {/* Name */}
              <Form.Group controlId="name">
                <Form.ControlLabel>Name *</Form.ControlLabel>
                <Form.Control
                  name="name"
                  placeholder="Enter full name"
                  prefix={<FaUser />}
                />
                <Form.ErrorMessage name="name" />
              </Form.Group>

              {/* Email */}
              <Form.Group controlId="email">
                <Form.ControlLabel>Email Address *</Form.ControlLabel>
                <Form.Control
                  name="email"
                  type="email"
                  placeholder="user@example.com"
                  prefix={<FaEnvelope />}
                />
                <Form.ErrorMessage name="email" />
              </Form.Group>

              {/* Phone */}
              <Form.Group controlId="phone">
                <Form.ControlLabel>Phone Number</Form.ControlLabel>
                <Form.Control
                  name="phone"
                  placeholder="e.g., 01712345678"
                  prefix={<FaPhone />}
                />
                <Form.ErrorMessage name="phone" />
              </Form.Group>

              {/* Password */}
              <Form.Group controlId="password">
                <Form.ControlLabel>Password *</Form.ControlLabel>
                <Form.Control
                  name="password"
                  type="password"
                  placeholder="Enter password (min 8 characters)"
                  prefix={<FaKey />}
                />
                <Form.HelpText className="text-muted">
                  Must be at least 8 characters long.
                </Form.HelpText>
                <Form.ErrorMessage name="password" />
              </Form.Group>

              {/* Role */}
              <Form.Group controlId="who_am_i">
                <Form.ControlLabel>Role *</Form.ControlLabel>
                <Form.Control
                  name="who_am_i"
                  accepter={SelectPicker}
                  data={roleOptions}
                  labelKey="label"
                  valueKey="value"
                  renderMenuItem={renderMenuItem}
                  placeholder="Select Role"
                  style={{ width: "100%" }}
                  // 🔥 Critical Fix:
                  renderMenuOnBody // Prevents ResizeObserver issues in scrollable containers
                  preventOverflow
                />
                <Form.ErrorMessage name="who_am_i" />
              </Form.Group>

              {/* Action Buttons */}
              <div className="d-flex gap-3 pt-3 border-top" style={{ marginTop: "2rem" }}>
                <Button
                className="custom-bg"
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Creating...
                    </>
                  ) : (
                    "Add User"
                  )}
                </Button>

                <Button
                  className="border"
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUser;